import { motion } from "framer-motion";
import { Award, Clock } from "lucide-react";
import ChapterHeader from "./ChapterHeader";
import { CONTENT } from "../data/content";

function CertBadge({ code, title, level, issuer, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="group relative bg-surface border border-border p-8 md:p-10 overflow-hidden hover:border-accent transition-colors"
      data-testid={`cert-badge-${code}`}
    >
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-accent/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
      <div className="relative flex items-start justify-between mb-6 md:mb-10">
        <div className="w-14 h-14 md:w-16 md:h-16 grid place-items-center border border-accent/60 bg-accent/10">
          <Award className="text-accent" size={22} />
        </div>
        <div className="font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-muted-foreground">
          {code}
        </div>
      </div>
      <div className="relative">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent mb-2">
          {level}
        </div>
        <h3 className="font-heading text-2xl md:text-3xl font-black tracking-tight leading-tight uppercase">
          {title}
        </h3>
        <div className="mt-6 pt-6 border-t border-border font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
          {issuer}
        </div>
      </div>
    </motion.div>
  );
}

export default function Certifications() {
  return (
    <section
      id="certifications"
      data-testid="certifications-section"
      className="relative border-t border-border py-24 md:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <ChapterHeader n="03" kicker="Credentials" title={<>Certified<br /><span className="text-accent">& proven</span>.</>}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {CONTENT.certifications.achieved.map((c, i) => (
              <CertBadge key={c.code} index={i} {...c} />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-12 md:mt-16 border-t border-border pt-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <Clock size={14} className="text-muted-foreground" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                In progress
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              {CONTENT.certifications.inProgress.map((c) => (
                <div
                  key={c.short}
                  data-testid={`cert-inprogress-${c.short}`}
                  className="flex items-center gap-3 border border-dashed border-border px-4 py-2.5 hover:border-accent transition-colors"
                >
                  <span className="font-mono text-xs text-accent">{c.short}</span>
                  <span className="text-sm text-foreground/80">{c.title}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </ChapterHeader>
      </div>
    </section>
  );
}
